import React from 'react';
import { useSelector } from 'react-redux';

import {
  DATE_RANGE_PRESET_LABELS,
  DATE_RANGE_PRESETS,
} from '@frontend/api/src/notifications/constants';
import { Flex, Text } from '@frontend/legacy-components';

import type {
  FiltersType,
  OrganizationIds,
  AppliedFilterOrganizations,
  AvailableGroupMembers,
} from 'modules/notifications/types/index';

import { getAvailableGroupMembers } from 'store/session/selectors';

const getNotificationFilterOrgs = (
  organizationIds: OrganizationIds,
  availableGroupMembers: AvailableGroupMembers | never[],
) => {
  return organizationIds.map((id: number) => {
    for (const { group } of availableGroupMembers) {
      if (id === group.id) return group.businessName;
    }
  });
};

/**
 *  Returns an array of of two objects, orgData, and dateData, enabling us to display all relevant applied filter information as a single string, matching quotes filter implementation and design
 */
const generateFilterDisplayInformation = (
  dateMax: string | undefined,
  dateMin: string | undefined,
  dateRange: string | undefined,
  orgs: string[] | null,
) => {
  const filterDisplayInformation = [];

  const orgData = { intro: '', value: '' };
  const dateData = { intro: 'Date range is', value: '' };

  if (orgs && orgs.length === 1) {
    orgData.intro = 'Organization is';
    orgData.value = `${orgs[0]}`;
  }

  if (orgs && orgs.length > 1) {
    orgData.intro = 'Organizations are';
    orgData.value = `${orgs.join(', ')}`;
  }

  if (dateRange && dateRange !== DATE_RANGE_PRESETS.NONE)
    dateData.value = DATE_RANGE_PRESET_LABELS[dateRange];

  if (dateMax && dateMin) dateData.value = 'Custom Range';

  if (Object.values(orgData).every((val) => val))
    filterDisplayInformation.push(orgData);

  if (dateData.value) filterDisplayInformation.push(dateData);

  return filterDisplayInformation;
};

const AppliedFiltersDisplay = ({ filters }: { filters: FiltersType }) => {
  const availableGroupMembers: AvailableGroupMembers | never[] = useSelector(
    getAvailableGroupMembers,
  );
  const { organizationIds, dateRange, dateMax, dateMin } = filters;

  const appliedFilterOrganizations: AppliedFilterOrganizations = organizationIds
    ? getNotificationFilterOrgs(organizationIds, availableGroupMembers)
    : null;

  const filterDisplayInformation = generateFilterDisplayInformation(
    dateMin,
    dateMax,
    dateRange,
    appliedFilterOrganizations,
  );

  return (
    <Flex alignItems="center" paddingRight={1}>
      <Text as="span" fontWeight="bold" whiteSpace="nowrap">
        FILTERS APPLIED{' '}
      </Text>
      <Text
        as="span"
        backgroundColor="feature.placeholder"
        height="1px"
        width="22px"
        marginLeft={0.5}
        marginRight={0.5}
      />
      {
        <Text as="span">
          {filterDisplayInformation.map((filterData, idx) => (
            <React.Fragment key={`${filterData.intro}-${filterData.value}`}>
              {filterData.intro} <b>{filterData.value}</b>
              {idx === filterDisplayInformation.length - 1 ? '' : <b>{', '}</b>}
            </React.Fragment>
          ))}
        </Text>
      }
    </Flex>
  );
};

export default AppliedFiltersDisplay;
